<template>
  <!-- PDFビュアー -->
  <div v-if="photostream" class="pdf-previewer">
    <preview-pdf-view
      :id="id"
      :file="path"
      :isMobile="isMobile"
      @load="onLoadImage"
      class="pdf-viewer"
      :saveAllowed="downloadable"
    />
  </div>
  <!-- 一覧画面の添付ファイル -->
  <div v-else :class="{ 'my-3': true, 'w-100': true, 'px-3': true }">
    <b-overlay
      class="pdf-grey-box d-flex flex-column justify-content-center align-items-center"
      no-center
      :show="true"
      :opacity="0"
    >
      <div class="loaded-tmb">
        <img
          loading="lazy"
          :src="pdfThumbnail"
          :class="{
            'mx-auto': true,
            'pdf-icon': isPdfThumbnail,
            'frame-border': isPdfThumbnail,
            'list-icon-size': !isPdfThumbnail,
          }"
          @load="onLoadImageWrapper"
          @error="setDefaultIcon"
        />
        <p v-if="isPdfThumbnail && showPdfLabel" class="pdf-tmb-label">PDF</p>
      </div>
      <div class="file-name dark">{{ imageFileName }}</div>
      <template #overlay>
        <img :src="blankGifUrl" class="w-100 dummyCover" />
      </template>
    </b-overlay>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import PreviewPdfView from "../preview_pdf/PreviewPdfView.vue";
import blankGif from "@/assets/blank.gif";

// NOTE: 旧コードでは CustomButton も登録されていたが、テンプレート未使用の
// デッド登録だったため除去（振る舞い不変）。
export default defineComponent({
  components: {
    PreviewPdfView,
  },
  props: {
    photostream: { type: Boolean, default: false },
    pdfFront: { type: Boolean, default: false },
    id: { type: String, default: "" },
    path: { type: String, default: "" },
    isMobile: { type: Boolean, default: false },
    downloadable: { type: Boolean, default: false },
    thumbnailPath: { type: String, default: "" },
    pdfThumbnail: { type: String, default: "" },
    isPdfThumbnail: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    imageFileName: { type: String, default: "" },
    allowDownload: { type: Boolean, default: false },
    downloadNote: { type: String, default: "" },
    onLoadImage: { type: Function as PropType<() => void>, default: Function },
    pdfIconClick: { type: Function as PropType<() => void>, default: Function },
    downloadFile: { type: Function as PropType<() => void>, default: Function },
  },
  setup(props, { emit }) {
    // $el は Vue 2 インスタンス専用 API のため getCurrentInstance 経由で参照する
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    // サムネイルの幅を保持するための変数
    const thumbnailWidth = ref<number>(0);

    const showPdfLabel = computed(() => {
      return thumbnailWidth.value > 44;
    });

    const blankGifUrl = computed(() => {
      return blankGif;
    });

    const updateThumbnailWidth = () => {
      // サムネイル画像の要素を取得
      const img = vm?.$el.querySelector('img[loading="lazy"]');
      if (img && img instanceof HTMLImageElement) {
        thumbnailWidth.value = img.clientWidth;
      }
    };

    // 画像ロード時にも幅を更新
    const onLoadImageWrapper = () => {
      updateThumbnailWidth();
      if (typeof props.onLoadImage === "function") {
        props.onLoadImage();
      }
    };

    const setDefaultIcon = (): void => {
      emit("loadError");
    };

    onMounted(() => {
      nextTick(() => {
        updateThumbnailWidth();
        window.addEventListener("resize", updateThumbnailWidth);
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", updateThumbnailWidth);
    });

    return {
      thumbnailWidth,
      showPdfLabel,
      blankGifUrl,
      updateThumbnailWidth,
      onLoadImageWrapper,
      setDefaultIcon,
    };
  },
});
</script>

<style lang="scss" scoped>
.pdf-previewer {
  height: 85vh;
  width: 85vw;
}

.pdf-grey-box {
  width: 100%;
}
.loaded-tmb {
  position: relative;
  margin-bottom: 8px;
  .pdf-tmb-label {
    top: 0 !important;
    left: 0 !important;
    transform: none !important;
    font-size: 10px !important;
    font-weight: bold !important;
    background: #dd1e03;
    color: white;
    padding: 0 2px !important;
    position: absolute;
    border-radius: 2px;
  }
}
.default-tmb {
  display: grid;
  justify-items: center;
  align-items: center;
  width: 178px;
  height: 178px;
  margin-bottom: 8px;
  border-radius: 8px;
  position: relative;
  background-color: white;
}
.pdf-tmb-label {
  top: 0 !important;
  left: 0 !important;
  transform: none !important;
  font-size: 10px !important;
  font-weight: bold !important;
  background: #dd1e03;
  color: white;
  padding: 0 2px !important;
  position: absolute;
  border-radius: 2px;
}
.frame-border {
  max-width: 100%;
  position: relative;
  border: 0.5px black solid;
}
.preview-icon-size {
  height: 96px;
}
.pdf-icon {
  width: 59px;
  height: 73px;
  object-fit: cover;
}
.list-icon-size {
  height: 72px;
  width: 60px;
}
.file-name {
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  color: white;
  &.dark {
    color: #707070;
  }
}
.attention-label {
  padding: 8px 4px 0 4px;
  color: white;
  font-size: 12px;
}
.dummyCover {
  height: 100%;
  z-index: 1;
}
</style>
